import { Skeleton } from "@/components/ui/Skeleton";
import {
  RiPlaneLine,
  RiMapPinLine,
  RiPhoneLine,
  RiPrinterLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiUserLine,
  RiSunLine,
  RiArrowRightLine,
  RiTimeLine,
  RiRestaurantLine,
  RiCarLine,
  RiAddLine,
  RiCloseLine,
} from "@remixicon/react";

// Inline editable input that blends with the document
const Field = ({
  value,
  onChange,
  placeholder,
  className = "",
  inputClassName = "",
  readOnly = false,
}) =>
  readOnly ? (
    <span className={`block ${className}`}>{value || placeholder}</span>
  ) : (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-transparent outline-none transition-colors border-b border-dashed border-gray-300 focus:border-primary-400 ${inputClassName} ${className}`}
    />
  );

// Field for white-on-dark sections
const LightField = ({ value, onChange, placeholder, className = "", readOnly = false }) =>
  readOnly ? (
    <span className={`text-white ${className}`}>{value || placeholder}</span>
  ) : (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-transparent outline-none text-white transition-colors border-b border-dashed border-white/30 focus:border-white/60 placeholder:text-white/80 ${className}`}
    />
  );

const SectionLabel = ({ children, onAdd, addLabel }) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-white/70 text-xs font-semibold uppercase tracking-wider">
      {children}
    </span>
    {onAdd && (
      <button
        onClick={onAdd}
        className="no-print flex items-center gap-1 text-white/80 hover:text-white/80 transition-colors"
      >
        <RiAddLine className="w-3.5 h-3.5" />
        <span className="text-[10px] tracking-normal font-medium">
          {addLabel}
        </span>
      </button>
    )}
  </div>
);

const WeatherFields = ({ data, basePath, onChange, brandColor }) => (
  <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-2">
    <RiSunLine className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
    <Field
      value={data.weather}
      onChange={(v) => onChange(`${basePath}.weather`, v)}
      placeholder="Weather"
      className="w-16 text-xs"
    />
    <Field
      value={data.tempHi}
      onChange={(v) => onChange(`${basePath}.tempHi`, v)}
      placeholder="Hi"
      className={`w-8 text-xs font-medium ${brandColor ? '' : 'text-primary-500'}`}
      style={brandColor ? { color: brandColor } : undefined}
    />
    <span>/</span>
    <Field
      value={data.tempLo}
      onChange={(v) => onChange(`${basePath}.tempLo`, v)}
      placeholder="Lo"
      className="w-8 text-xs"
    />
  </div>
);

const AirportFields = ({ title, data, basePath, onChange, brandColor }) => (
  <div>
    <p className="text-[10px] uppercase text-gray-400 tracking-wider mb-1">
      {title}
    </p>
    <Field
      value={data.airportName}
      placeholder="Airport Name"
      className="text-sm font-bold text-gray-900 w-full"
      readOnly
    />
    <Field
      value={data.address}
      placeholder="Airport Address"
      className="text-xs text-gray-500 mt-0.5 w-full"
      readOnly
    />
    <Field
      value={data.contactNumber}
      onChange={(v) => onChange(`${basePath}.contactNumber`, v)}
      placeholder="Contact Number"
      className={`text-xs mt-1.5 w-full ${brandColor ? '' : 'text-primary-500'}`}
      style={brandColor ? { color: brandColor } : undefined}
    />
    <p className="text-[10px] uppercase text-gray-400 tracking-wider mt-2">
      Weather
    </p>
    <WeatherFields data={data} basePath={basePath} onChange={onChange} brandColor={brandColor} />
  </div>
);

const serviceIcons = {
  catering: RiRestaurantLine,
  transportation: RiCarLine,
};

const TripSheetDocument = ({
  data,
  organization,
  isOrgLoading,
  onChange,
  onAddPassenger,
  onRemovePassenger,
  onAddCrew,
  onRemoveCrew,
  onAddService,
  onRemoveService,
  protectedCounts,
  readOnlyAircraft,
  fetchingArrivalTimes,
}) => {
  const brandColor = organization?.branding?.color;

  return (
    <div className="w-[310mm] mx-auto text-gray-900 font-rethink print:w-full p-4 space-y-3">
      {/* ===== HEADER ===== */}
      <div className="bg-white rounded-xl px-5 py-4 flex items-start justify-between">
        <div
          className="flex items-center justify-center shrink-0 rounded-lg overflow-hidden"
          style={{
            backgroundColor: brandColor || '#f3f4f6',
            width: 120,
            height: 80,
          }}
        >
          {isOrgLoading ? (
            <Skeleton className="h-full w-full" />
          ) : organization?.branding?.logo ? (
            <img src={`https://api.trustjet.ai:8000${organization.branding.logo}`} alt={organization.name || "Logo"} className="w-full h-full object-contain p-1.5" />
          ) : (
            <div className="flex items-center gap-1">
              <RiPlaneLine className="w-8 h-8 text-white" />
              <span className="text-2xl font-bold text-white">Logo</span>
            </div>
          )}
        </div>
        <div className="text-right text-sm space-y-0.5">
          <Field
            value={data.operator.name || "-"}
            onChange={(v) => onChange("operator.name", v)}
            placeholder="[Operator Company Name]"
            className="font-bold text-sm text-gray-900 text-right w-full"
            readOnly
          />
          <div className="flex items-center justify-end gap-1.5 text-gray-600">
            <RiMapPinLine className="w-3.5 h-3.5 shrink-0" />
            <Field
              value={data.operator.address || "-"}
              onChange={(v) => onChange("operator.address", v)}
              placeholder="[Operator Address]"
              className="text-xs text-right w-full"
              readOnly
            />
          </div>
          <div className="flex items-center justify-end gap-1.5 text-gray-600">
            <RiPhoneLine className="w-3.5 h-3.5 shrink-0" />
            <Field
              value={data.operator.phone || "-"}
              onChange={(v) => onChange("operator.phone", v)}
              placeholder="[Operator Phone Number]"
              className="text-xs text-right w-full"
              readOnly
            />
          </div>
          <div className="flex items-center justify-end gap-1.5 text-gray-600">
            <RiPrinterLine className="w-3.5 h-3.5 shrink-0" />
            <Field
              value={data.operator.fax || "-"}
              onChange={(v) => onChange("operator.fax", v)}
              placeholder="[Operator Fax Number]"
              className="text-xs text-right w-full"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* ===== CLIENT BAR ===== */}
      <div className={`rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm ${brandColor ? '' : 'bg-primary-500'}`} style={brandColor ? { backgroundColor: brandColor } : undefined}>
        <LightField
          value={data.client.name || "-"}
          onChange={(v) => onChange("client.name", v)}
          placeholder="[Client name]"
          className="font-medium flex-1 min-w-0 text-xs"
          readOnly
        />
        <LightField
          value={data.client.company || "-"}
          onChange={(v) => onChange("client.company", v)}
          placeholder="[Client company name]"
          className="flex-1 min-w-0 text-xs"
          readOnly
        />
        <LightField
          value={data.client.email || "-"}
          onChange={(v) => onChange("client.email", v)}
          placeholder="[Client email]"
          className="flex-[1.3] min-w-0 text-xs"
          readOnly
        />
        <LightField
          value={data.client.phone || "-"}
          onChange={(v) => onChange("client.phone", v)}
          placeholder="[Client phone number]"
          className="flex-1 min-w-0 text-xs"
          readOnly
        />
      </div>

      <div className={`rounded-xl p-4 space-y-3 ${brandColor ? '' : 'bg-primary-500'}`} style={brandColor ? { backgroundColor: brandColor } : undefined}>

      {/* ===== LEG SUMMARY ===== */}
      <div className="grid grid-cols-[300px_1fr] gap-3">
        {/* Aircraft Panel */}
        <div className="bg-white rounded-xl p-4 flex flex-col justify-between">
          <div className="bg-gray-100 rounded-lg h-28 flex items-center justify-center">
            {data.aircraft.image ? (
              <img
                src={data.aircraft.image}
                alt={data.aircraft.name}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : null}
          </div>
          <div className="mt-3 overflow-hidden">
            <Field
              value={data.aircraft.tailNumber}
              onChange={(v) => onChange("aircraft.tailNumber", v)}
              placeholder="Tail Number"
              className="text-xl font-bold w-full text-gray-900"
              readOnly={readOnlyAircraft}
            />
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
              <span className="flex items-center gap-1">
                <RiPlaneLine className="w-3 h-3 shrink-0" />
                <Field
                  value={data.aircraft.name}
                  onChange={(v) => onChange("aircraft.name", v)}
                  placeholder="Aircraft Name"
                  className="text-xs w-auto"
                  readOnly={readOnlyAircraft}
                />
              </span>
              <span className="flex items-center gap-1">
                <RiUserLine className="w-3 h-3 shrink-0" />
                <Field
                  value={String(data.aircraft.passengerCount)}
                  onChange={(v) => onChange("aircraft.passengerCount", v)}
                  placeholder="0"
                  className="text-xs"
                  readOnly={readOnlyAircraft}
                />
              </span>
            </div>
          </div>
        </div>

        {/* Leg Summary Table */}
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-base">Leg Summary</h3>
          </div>
          <div className="space-y-2">
            {data.legs.map((leg, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 text-xs text-gray-600 flex-wrap"
              >
                <span className="font-semibold text-gray-900 shrink-0">
                  LEG {leg.legNumber}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  <RiFlightTakeoffLine className={`w-3 h-3 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                  <Field value={leg.departure.code} placeholder="CODE" className="w-12 text-xs font-medium" readOnly />
                </span>
                <Field value={leg.departure.date} placeholder="Departure Date" className="w-24 text-xs" readOnly />
                <span className="flex items-center gap-1 shrink-0">
                  <RiTimeLine className="w-3 h-3" />
                  <Field value={leg.departure.time} placeholder="Time" className="w-14 text-xs" readOnly />
                </span>
                <RiArrowRightLine className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="flex items-center gap-1 shrink-0">
                  <RiFlightLandLine className={`w-3 h-3 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                  <Field value={leg.arrival.code} placeholder="CODE" className="w-12 text-xs font-medium" readOnly />
                </span>
                <Field value={leg.arrival.date} placeholder="Arrival Date" className="w-24 text-xs" readOnly />
                <span className="flex items-center gap-1 shrink-0">
                  <RiTimeLine className="w-3 h-3" />
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-4 w-14 rounded" />
                  ) : (
                    <Field
                      value={leg.arrival.time}
                      placeholder="-"
                      className="w-14 text-xs"
                      readOnly
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== LEG DETAILS ===== */}
      {data.legs.map((leg, idx) => (
        <div key={idx} className="space-y-3">
          {idx > 0 && (
            <div className="py-4"><div className="border-t-2 border-dashed border-white/30" /></div>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70 text-xs font-semibold uppercase tracking-wider">
              Leg {leg.legNumber}
            </span>
          </div>
          <div className="bg-white rounded-xl p-4 grid grid-cols-[1fr_1fr_1fr] gap-4">
            {/* Left: Codes & Times */}
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-3">
                <div>
                <p className="text-gray-400 text-[10px] uppercase">
                  TIMEZONE
                </p>
                <Field
                  value={leg.timezone}
                  onChange={(v) => onChange(`legs.${idx}.timezone`, v)}
                  placeholder="TZ"
                  className="text-xs font-medium w-full"
                  readOnly
                />
                </div>
                <div className="ml-auto text-right">
                <p className="text-gray-400 text-[10px] uppercase">
                  DISTANCE
                </p>
                <Field
                  value={leg.flightDistance}
                  onChange={(v) => onChange(`legs.${idx}.flightDistance`, v)}
                  placeholder="Distance"
                  className="text-xs font-medium w-full text-right"
                  readOnly
                />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <Field value={leg.departure.code} placeholder="CODE" className="text-3xl font-black text-gray-900 w-24" readOnly />
                <div className="flex flex-col items-center">
                  <RiPlaneLine className={`w-4 h-4 rotate-90 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                </div>
                <Field value={leg.arrival.code} placeholder="CODE" className="text-3xl font-black text-gray-900 w-24" readOnly />
              </div>
              <div className="grid grid-cols-2 gap-x-4 mt-3 text-xs text-gray-600">
                <div>
                  <p className="text-gray-400 text-[10px] uppercase">
                    Departure
                  </p>
                  <Field value={leg.departure.time} placeholder="Time" className="text-xs font-medium w-full" readOnly />
                  <Field value={leg.departure.date} placeholder="Date" className="text-xs w-full" readOnly />
                  <Field value={leg.departure.day} placeholder="Day" className="text-xs w-full" readOnly />
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-[10px] uppercase">
                    Arrival
                  </p>
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-4 w-14 rounded ml-auto" />
                  ) : (
                    <Field
                      value={leg.arrival.time}
                      placeholder="-"
                      className="text-xs font-medium w-full text-right"
                      readOnly
                    />
                  )}
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-3 w-16 rounded ml-auto" />
                  ) : (
                    <Field value={leg.arrival.date} placeholder="Date" className="text-xs w-full text-right" readOnly />
                  )}
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-3 w-12 rounded ml-auto" />
                  ) : (
                    <Field value={leg.arrival.day} placeholder="Day" className="text-xs w-full text-right" readOnly />
                  )}
                </div>
              </div>
              <p className="text-gray-400 text-[10px] uppercase mt-3">
                DURATION
              </p>
              {fetchingArrivalTimes ? (
                <Skeleton className="h-3 w-16 rounded mt-2" />
              ) : (
                <Field
                  value={leg.flightDuration}
                  onChange={(v) => onChange(`legs.${idx}.flightDuration`, v)}
                  placeholder="Duration"
                  className="text-xs font-medium w-full"
                  readOnly
                />
              )}
            </div>

            {/* Center: Departure Airport */}
            <div className="border-l border-gray-200 pl-4">
              <AirportFields
                title="Departure"
                data={leg.departure}
                basePath={`legs.${idx}.departure`}
                onChange={onChange}
                brandColor={brandColor}
              />
            </div>

            {/* Right: Arrival Airport */}
            <div className="border-l border-gray-200 pl-4">
              <AirportFields
                title="Arrival"
                data={leg.arrival}
                basePath={`legs.${idx}.arrival`}
                onChange={onChange}
                brandColor={brandColor}
              />
            </div>
          </div>

      {/* ===== CREW & PASSENGERS for this leg ===== */}
      {(leg.crew.length > 0 || leg.passengers.length > 0) && (
      <div className="grid grid-cols-[1fr_1.5fr] gap-3">
        {/* Crew */}
        {leg.crew.length > 0 && (
        <div>
          <SectionLabel addLabel="Add Crew">
            Crew
          </SectionLabel>
          <div className="bg-white rounded-xl p-4">
            <div className="grid grid-cols-2 gap-3">
              {leg.crew.map((member, i) => {
                const isProtected = i < (protectedCounts?.legs?.[idx]?.crew ?? 0);
                return (
                <div key={i} className="flex items-start gap-1 group bg-gray-100 p-1 rounded-md">
                  <div className="flex-1">
                    <Field
                      value={member.name}
                      onChange={(v) => onChange(`legs.${idx}.crew.${i}.name`, v)}
                      placeholder={`Crew Name ${i + 1}`}
                      className="text-sm font-semibold text-gray-900 w-full"
                      readOnly
                    />
                    <Field
                      value={member.phone}
                      onChange={(v) => onChange(`legs.${idx}.crew.${i}.phone`, v)}
                      placeholder="Phone Number"
                      className="text-xs text-gray-500 w-full"
                      readOnly
                    />
                  </div>
                  {!isProtected && (
                  <button
                    onClick={() => onRemoveCrew(idx, i)}
                    className="no-print opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all mt-1 shrink-0"
                    title="Remove crew member"
                  >
                    <RiCloseLine className="w-3.5 h-3.5" />
                  </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {/* Passengers */}
        {leg.passengers.length > 0 && (
        <div>
          <SectionLabel addLabel="Add Passenger">
            Passengers
          </SectionLabel>
          <div className="bg-white rounded-xl p-4">
            <div className="grid grid-cols-2 gap-2">
              {leg.passengers.map((passenger, i) => {
                const isProtected = i < (protectedCounts?.legs?.[idx]?.passengers ?? 0);
                return (
                <div
                  key={i}
                  className="bg-gray-100 rounded-md px-2.5 py-1.5 flex items-center gap-1 group"
                >
                  <Field
                    value={typeof passenger === "object" ? passenger.fullName || "" : passenger}
                    onChange={(v) => {
                      const updated = [...leg.passengers];
                      updated[i] = typeof passenger === "object" ? { ...passenger, fullName: v } : v;
                      onChange(`legs.${idx}.passengers`, updated);
                    }}
                    placeholder={`Passenger Name ${i + 1}`}
                    className="text-sm w-full border-none"
                    readOnly
                  />
                  {!isProtected && (
                  <button
                    onClick={() => onRemovePassenger(idx, i)}
                    className="no-print opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all shrink-0"
                    title="Remove passenger"
                  >
                    <RiCloseLine className="w-3.5 h-3.5" />
                  </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
        )}
      </div>
      )}

      {/* ===== ADDITIONAL SERVICES for this leg ===== */}
      {leg.additionalServices.length > 0 && (
      <div>
        <SectionLabel addLabel="Add Service">
          Additional Services
        </SectionLabel>
        <div className="bg-white rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider">
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">
                  Service Details
                </th>
                <th className="text-left px-3 py-2 font-medium">Address</th>
                <th className="text-left px-3 py-2 font-medium">
                  Phone Number
                </th>
                <th className="text-left px-3 py-2 font-medium">
                  Confirmation Number
                </th>
                <th className="no-print w-8"></th>
              </tr>
            </thead>
            <tbody>
              {leg.additionalServices.map((service, i) => {
                const Icon = serviceIcons[service.icon] || RiPlaneLine;
                const isServiceProtected = i < (protectedCounts?.legs?.[idx]?.services ?? 0);
                return (
                  <tr
                    key={i}
                    className="border-b border-gray-100 last:border-0 group"
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 shrink-0 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                        <Field
                          value={service.name}
                          onChange={(v) =>
                            onChange(`legs.${idx}.additionalServices.${i}.name`, v)
                          }
                          placeholder="Service Name"
                          className="text-sm font-medium w-full"
                          readOnly={isServiceProtected}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.serviceDetails}
                        onChange={(v) =>
                          onChange(`legs.${idx}.additionalServices.${i}.serviceDetails`, v)
                        }
                        placeholder="Service Details"
                        className="text-sm text-gray-600 w-full"
                        readOnly={isServiceProtected}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.address}
                        onChange={(v) =>
                          onChange(`legs.${idx}.additionalServices.${i}.address`, v)
                        }
                        placeholder="Address"
                        className="text-sm text-gray-600 w-full"
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.phone}
                        onChange={(v) =>
                          onChange(`legs.${idx}.additionalServices.${i}.phone`, v)
                        }
                        placeholder="Phone"
                        className="text-sm text-gray-600 w-full"
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.confirmationNumber}
                        onChange={(v) =>
                          onChange(
                            `legs.${idx}.additionalServices.${i}.confirmationNumber`,
                            v
                          )
                        }
                        placeholder="Confirmation #"
                        className="text-sm text-gray-600 w-full"
                      />
                    </td>
                    <td className="px-1 py-2.5">
                      {i >= (protectedCounts?.legs?.[idx]?.services ?? 0) && (
                      <button
                        onClick={() => onRemoveService(idx, i)}
                        className="no-print opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                        title="Remove service"
                      >
                        <RiCloseLine className="w-3.5 h-3.5" />
                      </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* <button
            onClick={() => onAddService(idx)}
            className={`no-print w-full border-t border-dashed border-gray-300 py-2 flex items-center justify-center gap-1.5 text-xs text-gray-400 transition-colors ${brandColor ? '' : 'hover:border-primary-400 hover:text-primary-500'}`}
              onMouseEnter={brandColor ? (e) => { e.currentTarget.style.color = brandColor; e.currentTarget.style.borderColor = brandColor; } : undefined}
              onMouseLeave={brandColor ? (e) => { e.currentTarget.style.color = ''; e.currentTarget.style.borderColor = ''; } : undefined}
          >
            <RiAddLine className="w-3.5 h-3.5" />
            Add Service
          </button> */}
        </div>
      </div>
      )}
        </div>
      ))}

      </div>

      {/* ===== FOOTER ===== */}
      <div className="text-center text-xs text-white/40 pb-2">
        Page 1 of 2
      </div>
    </div>
  );
};

export default TripSheetDocument;